import React, { KeyboardEvent } from 'react';
import { TextChoiceDefinition, SurveyFormStyle } from "../../../../types/Survey";
import SurveyMarkdown from "../SurveyMarkdown/SurveyMarkdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCheckSquare, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import "../../SurveyVars.css"
import "./TextChoiceList.css"
import { faCircle, faSquare } from "@fortawesome/pro-regular-svg-icons";

export interface TextChoiceListProps {
    selectedChoices: string[];
    onChangeChoices: (answer: string[]) => void;
    variant?: SurveyFormStyle;
    textChoices: TextChoiceDefinition[];
    style: "Single" | "Multiple";
    descriptionStyle?: "displayWhenExpanded" | "displayAlways";
}

export default function (props: TextChoiceListProps) {
    const groupName = React.useId();
    
    function setChoice(choice: TextChoiceDefinition) {
        if (props.style == "Single") {
            let newChoices = props.selectedChoices.includes(choice.value) ? [] : [choice.value];
            props.onChangeChoices(newChoices);
        } else if (props.style == "Multiple") {
            if (choice.exclusive) {
                if (props.selectedChoices.includes(choice.value)) {
                    props.onChangeChoices([]);
                } else {
                    props.onChangeChoices([choice.value]);
                }
            }
            else {
                let newChoices = props.selectedChoices.filter(c => !props.textChoices.find(tc => tc.value == c)?.exclusive);
                if (newChoices.includes(choice.value)) {
                    newChoices = newChoices.filter(c => c != choice.value);
                } else {
                    newChoices.push(choice.value);
                }
                props.onChangeChoices(newChoices);
            }
        }
    }

    let classes = ["text-choice-list"];
    if (props.variant == "cardBased") {
        classes.push("text-choice-list-card-based");
    }
    if (props.variant == "v2") {
        classes.push("text-choice-list-v2");
    }

    return <div className={classes.join(" ")}>
        {props.textChoices.map(choice =>
            <TextChoice key={choice.value}
                choice={choice}
                style={props.style}
                selected={props.selectedChoices.includes(choice.value)}
                onClick={() => setChoice(choice)}
                groupName={groupName} 
                descriptionStyle={props.descriptionStyle} />
        )}
    </div>
}

interface TextChoiceProps {
    choice: TextChoiceDefinition;
    style: "Single" | "Multiple";
    selected: boolean;
    onClick: () => void;
    descriptionStyle?: "displayWhenExpanded" | "displayAlways";
    groupName?: string;
}

function TextChoice(props: TextChoiceProps) {
    let [showDescription, setShowDescription] = React.useState(false);
    const uniqueId = React.useId();

    let selectedIcon = props.style == "Single" ? faCheckCircle : faCheckSquare;
    let unselectedIcon = props.style == "Single" ? faCircle : faSquare;

    let classes = ["text-choice"];
    if (props.selected) {
        classes.push("text-choice-selected");
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            props.onClick()
        }
    };

    return <div className={classes.join(" ")}
            onKeyDown={handleKeyDown}>
        {/* Hidden input for screen readers and keyboard navigation - synced with visual state */}
        <input className="text-choice-icon sr-only" 
            type={props.style == "Single" ? "radio" : "checkbox"}
            id={uniqueId}
            name={props.groupName}
            value={props.choice.value}
            checked={props.selected}
            readOnly
        >
        </input>
        <label className="text-choice-label" 
            htmlFor={uniqueId}
            onClick={() => props.onClick()}
        >   <div className="text-choice-text">
                <SurveyMarkdown text={props.choice.text} inline />
                {props.choice.description && props.descriptionStyle == "displayWhenExpanded" &&
                    <span className="info-button" onClick={(e) => {
                        setShowDescription(!showDescription);
                        e.stopPropagation();
                    }}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </span>
                }
                {props.choice.description && (showDescription || props.descriptionStyle == "displayAlways") &&
                    <div className="text-choice-description">
                        <SurveyMarkdown text={props.choice.description} inline />
                    </div>
                }
            </div>
            {/* Visual custom checkbox/radio using FontAwesome icons */}
            <div className="text-choice-icon">
                <FontAwesomeIcon icon={props.selected ? selectedIcon : unselectedIcon} />
            </div>
        </label>
    </div>
}
